import React, { useState, useEffect } from 'react';
import { promocodesAPI, catalogAPI, placesAPI } from '../services/api';
import { Plus, Trash2, Tag, ChevronDown, ChevronRight, Edit2, FolderPlus, Search, Download } from 'lucide-react';
import SearchableSelect from '../components/SearchableSelect';
import toast from 'react-hot-toast';

const PERIOD_PRESETS = [
  { label: 'Сегодня', days: 0 },
  { label: '7 дней', days: 7 },
  { label: '30 дней', days: 30 },
  { label: '90 дней', days: 90 },
  { label: 'Всё время', days: null },
];

function PromoForm({ initial, onSubmit, onClose, groups, places }) {
  const isEdit = !!initial;
  const [form, setForm] = useState({
    code: '', description: '', discount_type: 'percent', discount_value: '',
    min_order_amount: 0, max_discount: '', max_uses: '', max_uses_per_client: 1,
    allow_with_bonus: true, valid_from: '', valid_until: '',
    restriction_mode: 'all', purchase_condition: 'any', group_id: '',
    ...(initial || {}),
  });
  const [allowedProducts, setAllowedProducts] = useState([]);
  const [allowedCategories, setAllowedCategories] = useState([]);
  const [allowedPlaces, setAllowedPlaces] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => { catalogAPI.categories().then(r => setCategories(r.data || [])).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => { catalogAPI.products(0, 50, null, productSearch || null, false).then(r => setProducts(r.data?.items || [])).catch(() => {}); }, 300);
    return () => clearTimeout(t);
  }, [productSearch]);
  useEffect(() => {
    if (initial?.allowed_place_ids?.length) setAllowedPlaces(places.filter(p => initial.allowed_place_ids.includes(String(p.id))));
  }, [initial]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code) { toast.error('Введите код'); return; }
    if (!form.discount_value) { toast.error('Введите скидку'); return; }
    setLoading(true);
    try {
      const data = { ...form, discount_value: parseFloat(form.discount_value), min_order_amount: parseFloat(form.min_order_amount) || 0,
        max_discount: form.max_discount ? parseFloat(form.max_discount) : null, max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        max_uses_per_client: parseInt(form.max_uses_per_client) || 1, valid_from: form.valid_from || null, valid_until: form.valid_until || null,
        group_id: form.group_id || null,
        allowed_product_ids: form.restriction_mode === 'products' ? allowedProducts.map(p => String(p.id)) : null,
        allowed_category_ids: form.restriction_mode === 'categories' ? allowedCategories.map(c => String(c.id)) : null,
        allowed_place_ids: allowedPlaces.length > 0 ? allowedPlaces.map(p => String(p.id)) : null,
      };
      await onSubmit(data); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Ошибка'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560, maxHeight: '90vh', overflowY: 'auto' }}>
        <h2 className="modal-title">{isEdit ? 'Редактировать' : 'Новый промокод'}</h2>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label className="form-label">Код *</label>
              <input className="form-input" value={form.code} disabled={isEdit} onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="WELCOME" /></div>
            <div className="form-group"><label className="form-label">Группа</label>
              <select className="form-input" value={form.group_id || ''} onChange={e => setForm({ ...form, group_id: e.target.value })}>
                <option value="">Без группы</option>
                {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
              </select></div>
          </div>
          <div className="form-group"><label className="form-label">Описание</label>
            <input className="form-input" value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label className="form-label">Тип скидки</label>
              <select className="form-input" value={form.discount_type} onChange={e => setForm({ ...form, discount_type: e.target.value })}>
                <option value="percent">Процент</option><option value="fixed">Фиксированная</option></select></div>
            <div className="form-group"><label className="form-label">Размер скидки *</label>
              <input className="form-input" type="number" min="0" step="0.01" value={form.discount_value} onChange={e => setForm({ ...form, discount_value: e.target.value })} /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="form-group"><label className="form-label">Мин. сумма</label>
              <input className="form-input" type="number" min="0" value={form.min_order_amount} onChange={e => setForm({ ...form, min_order_amount: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">Макс. скидка</label>
              <input className="form-input" type="number" min="0" value={form.max_discount || ''} onChange={e => setForm({ ...form, max_discount: e.target.value })} placeholder="∞" /></div>
            <div className="form-group"><label className="form-label">Макс. исп.</label>
              <input className="form-input" type="number" min="1" value={form.max_uses || ''} onChange={e => setForm({ ...form, max_uses: e.target.value })} placeholder="∞" /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label className="form-label">Условие покупки</label>
              <select className="form-input" value={form.purchase_condition} onChange={e => setForm({ ...form, purchase_condition: e.target.value })}>
                <option value="any">Любая покупка</option><option value="first">Только первая</option><option value="one">Одноразовый</option></select></div>
            <div className="form-group"><label className="form-label">Исп. на клиента</label>
              <input className="form-input" type="number" min="1" value={form.max_uses_per_client} onChange={e => setForm({ ...form, max_uses_per_client: e.target.value })} /></div>
          </div>
          <div className="form-group"><label className="form-label">Точки продаж</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
              {allowedPlaces.map(p => (<span key={p.id} className="badge badge-info" style={{ cursor: 'pointer' }}
                onClick={() => setAllowedPlaces(allowedPlaces.filter(x => x.id !== p.id))}>{p.name} ✕</span>))}
            </div>
            <select className="form-input" value="" onChange={e => {
              const place = places.find(p => p.id === e.target.value);
              if (place && !allowedPlaces.find(p => p.id === place.id)) setAllowedPlaces([...allowedPlaces, place]);
            }}><option value="">+ Добавить точку</option>
              {places.filter(p => !allowedPlaces.find(ap => ap.id === p.id)).map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
            </select></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group"><label className="form-label">Действует с</label>
              <input className="form-input" type="datetime-local" value={form.valid_from || ''} onChange={e => setForm({ ...form, valid_from: e.target.value })} /></div>
            <div className="form-group"><label className="form-label">Действует до</label>
              <input className="form-input" type="datetime-local" value={form.valid_until || ''} onChange={e => setForm({ ...form, valid_until: e.target.value })} /></div>
          </div>
          <label className="form-check" style={{ marginBottom: 16 }}>
            <input type="checkbox" checked={form.allow_with_bonus} onChange={e => setForm({ ...form, allow_with_bonus: e.target.checked })} />
            Можно использовать с бонусами</label>
          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Отмена</button>
            <button type="submit" className="btn btn-primary" disabled={loading}>{loading ? '...' : isEdit ? 'Сохранить' : 'Создать'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const conditionLabel = { any: 'Любая', first: 'Первая', one: 'Одноразовый' };

function formatMoney(v) { return v != null ? Math.round(v).toLocaleString('ru-RU') + ' ₽' : '—'; }
function roiColor(roi) { if (roi == null) return '#9ca3af'; if (roi >= 500) return '#22c55e'; if (roi >= 100) return '#84cc16'; if (roi >= 0) return '#f59e0b'; return '#ef4444'; }

export default function PromocodesPage() {
  const [promos, setPromos] = useState([]);
  const [total, setTotal] = useState(0);
  const [groups, setGroups] = useState([]);
  const [places, setPlaces] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editPromo, setEditPromo] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState(new Set());
  const [showGroupCreate, setShowGroupCreate] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');


  const [search, setSearch] = useState('');
  const [tab, setTab] = useState('active');
  const [periodDays, setPeriodDays] = useState(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');

  const buildDateFrom = () => {
    if (dateFrom) return dateFrom;
    if (periodDays !== null && periodDays !== undefined) {
      const d = new Date();
      d.setDate(d.getDate() - periodDays);

      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    }
    return '';
  };

  const load = () => {
    const df = buildDateFrom();
    promocodesAPI.list(0, 200, search || undefined, tab, df || undefined, dateTo || undefined, sortBy, sortDir)
      .then(r => { setPromos(r.data.items || []); setTotal(r.data.total || 0); }).catch(() => {});
    promocodesAPI.groups().then(r => setGroups(r.data || [])).catch(() => {});
    placesAPI.list().then(r => setPlaces(r.data || [])).catch(() => {});
  };

  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [search, tab, periodDays, dateFrom, dateTo, sortBy, sortDir]);

  const toggleGroup = (id) => { const n = new Set(expandedGroups); n.has(id) ? n.delete(id) : n.add(id); setExpandedGroups(n); };

  const handleDelete = async (id) => {
    if (!window.confirm('Удалить промокод?')) return;
    try { await promocodesAPI.delete(id); toast.success('Удалено'); load(); } catch { toast.error('Ошибка'); }
  };
  const handleCreateGroup = async () => {
    if (!newGroupName.trim()) return;
    try { await promocodesAPI.createGroup({ name: newGroupName.trim() }); toast.success('Группа создана'); setNewGroupName(''); setShowGroupCreate(false); load(); } catch { toast.error('Ошибка'); }
  };
  const handleDeleteGroup = async (id) => {
    if (!window.confirm('Удалить группу?')) return;
    try { await promocodesAPI.deleteGroup(id); toast.success('Удалена'); load(); } catch { toast.error('Ошибка'); }
  };

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('desc'); }
  };
  const sortIcon = (col) => sortBy === col ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const ungrouped = promos.filter(p => !p.group_id);
  const grouped = groups.map(g => ({ ...g, promos: promos.filter(p => p.group_id === g.id) }));

  const renderTable = (items) => (
    <div className="table-container" style={{ marginTop: 0 }}>
      <table className="table" style={{ fontSize: 12 }}>
        <thead><tr>
          <th>Код</th><th>Описание</th>
          <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('discount_value')}>Скидка{sortIcon('discount_value')}</th>
          <th>Условие</th><th>Период</th>
          <th style={{ cursor: 'pointer' }} onClick={() => toggleSort('used_count')}>Заказы{sortIcon('used_count')}</th>
          <th style={{ cursor: 'pointer', minWidth: 80 }}>Выручка</th>
          <th style={{ minWidth: 70 }}>Расходы</th>
          <th style={{ minWidth: 50 }}>ROI</th>
          <th>Статус</th><th></th>
        </tr></thead>
        <tbody>
          {items.map(p => (
            <tr key={p.id}>
              <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{p.code}</td>
              <td style={{ color: '#6b7280', maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.description || '—'}</td>
              <td>{p.discount_type === 'percent' ? `${p.discount_value}%` : `${p.discount_value}₽`}</td>
              <td style={{ fontSize: 11 }}>{conditionLabel[p.purchase_condition] || '—'}</td>
              <td style={{ fontSize: 10, color: '#9ca3af', whiteSpace: 'nowrap' }}>
                {p.valid_from ? new Date(p.valid_from).toLocaleDateString('ru') : '∞'}
                {' — '}
                {p.valid_until ? new Date(p.valid_until).toLocaleDateString('ru') : '∞'}
              </td>
              <td style={{ fontFamily: 'var(--font-mono)' }}>
                <span>{p.used_in_period || p.order_count || 0} <span style={{ color: '#9ca3af', fontSize: 10 }}>/ {p.used_count}</span></span>
              </td>
              <td style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{formatMoney(p.revenue)}</td>
              <td style={{ fontFamily: 'var(--font-mono)', color: '#ef4444' }}>{formatMoney(p.discount_total)}</td>
              <td style={{ fontWeight: 700, color: roiColor(p.roi) }}>{p.roi != null ? `${p.roi}%` : '—'}</td>
              <td><span className={`badge ${p.is_active ? 'badge-success' : 'badge-danger'}`}>{p.is_active ? 'Акт' : 'Выкл'}</span></td>
              <td>
                <div style={{ display: 'flex', gap: 4 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditPromo(p); setShowCreate(true); }}><Edit2 size={13} /></button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(p.id)}><Trash2 size={13} /></button>
                </div>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={11} style={{ textAlign: 'center', padding: 24, color: '#9ca3af' }}>Нет промокодов</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="animate-in">
      <div className="page-header">
        <h1 className="page-title">Промокоды</h1>
        <p className="page-subtitle">{total} промокодов, {groups.length} групп</p>
      </div>


      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn btn-primary" onClick={() => { setEditPromo(null); setShowCreate(true); }}><Plus size={14} /> Промокод</button>
        <button className="btn btn-ghost" onClick={() => setShowGroupCreate(!showGroupCreate)}><FolderPlus size={14} /> Группа</button>
        <div style={{ flex: 1 }} />
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 9, color: '#9ca3af' }} />
          <input className="form-input" style={{ paddingLeft: 30, width: 220, fontSize: 12 }}
            placeholder="Поиск по коду или описанию..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>


      {showGroupCreate && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12, alignItems: 'center', padding: 10, background: 'var(--bg-secondary)', borderRadius: 8, border: '1px solid var(--border)' }}>
          <input className="form-input" style={{ flex: 1 }} placeholder="Название группы..." value={newGroupName}
            onChange={e => setNewGroupName(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleCreateGroup()} />
          <button className="btn btn-primary btn-sm" onClick={handleCreateGroup}>Создать</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowGroupCreate(false)}>✕</button>
        </div>
      )}


      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>

        <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
          {['active', 'archive', 'all'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              style={{ padding: '6px 14px', border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                background: tab === t ? 'var(--primary)' : 'transparent', color: tab === t ? '#fff' : 'var(--text-primary)' }}>
              {t === 'active' ? 'Действующие' : t === 'archive' ? 'Архивные' : 'Все'}
            </button>
          ))}
        </div>

        <div style={{ width: 1, height: 24, background: 'var(--border)' }} />


        {PERIOD_PRESETS.map(p => (
          <button key={p.label} className={`btn btn-sm ${periodDays === p.days && periodDays !== null ? 'btn-primary' : p.days === null && periodDays === null && !dateFrom ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => { setPeriodDays(p.days); setDateFrom(''); setDateTo(''); }} style={{ fontSize: 11 }}>
            {p.label}
          </button>
        ))}

        <div style={{ width: 1, height: 24, background: 'var(--border)' }} />


        <input type="date" className="form-input" style={{ width: 130, fontSize: 11 }} value={dateFrom}
          onChange={e => { setDateFrom(e.target.value); setPeriodDays(null); }} />
        <span style={{ fontSize: 11, color: '#9ca3af' }}>—</span>
        <input type="date" className="form-input" style={{ width: 130, fontSize: 11 }} value={dateTo}
          onChange={e => { setDateTo(e.target.value); setPeriodDays(null); }} />
      </div>


      {grouped.filter(g => g.promos.length > 0).map(g => (
        <div key={g.id} style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', background: 'var(--bg-secondary)',
            borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer' }} onClick={() => toggleGroup(g.id)}>
            {expandedGroups.has(g.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <Tag size={14} style={{ color: 'var(--primary)' }} />
            <span style={{ fontWeight: 600, flex: 1 }}>{g.name}</span>
            <span className="badge badge-info">{g.promos.length}</span>
            <button className="btn btn-danger btn-sm" onClick={e => { e.stopPropagation(); handleDeleteGroup(g.id); }} style={{ padding: 2 }}><Trash2 size={12} /></button>
          </div>
          {expandedGroups.has(g.id) && renderTable(g.promos)}
        </div>
      ))}


      {ungrouped.length > 0 && (
        <>
          {groups.length > 0 && grouped.some(g => g.promos.length > 0) && (
            <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, marginTop: 16 }}>Без группы</div>
          )}
          {renderTable(ungrouped)}
        </>
      )}

      {promos.length === 0 && !search && (
        <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Промокодов нет</div>
      )}

      {showCreate && (
        <PromoForm initial={editPromo} groups={groups} places={places}
          onClose={() => { setShowCreate(false); setEditPromo(null); }}
          onSubmit={async (data) => {
            if (editPromo) { await promocodesAPI.update(editPromo.id, data); toast.success('Сохранено'); }
            else { await promocodesAPI.create(data); toast.success('Создан'); }
            load();
          }} />
      )}
    </div>
  );
}